import { useEffect, useState } from "react";
import api from "@/lib/api";
import { PreprintLayer } from "@/components/PreprintLayer";
import { DEFAULT_ALIGNMENT, cloneAlign } from "@/lib/alignment";
import { Button } from "@/components/ui/button";
import { Move, Save, RotateCcw, Loader2, ArrowUp, ArrowDown, ArrowLeft, ArrowRight } from "lucide-react";
import { toast } from "sonner";

const SAMPLE = {
  patient_name: "Ramesh Kumar",
  age_gender: "34 / Male",
  drug_allergy: "No known allergy",
  date: "2026-06-01",
  notes: "Warm saline rinse twice daily. Review after 1 week.",
  medicines: [
    { name: "Tab. Augmentin 625mg", food: "After food", morning: "1", afternoon: "0", night: "1", duration: "5 days", total_qty: "10" },
    { name: "Tab. Ibuprofen 400mg", food: "After food", morning: "1", afternoon: "1", night: "1", duration: "3 days", total_qty: "9" },
    { name: "Cap. Pantoprazole 40mg", food: "Before food", morning: "1", afternoon: "0", night: "0", duration: "5 days", total_qty: "5" },
  ],
};

export default function Alignment() {
  const [offset, setOffset] = useState(null); // { offsetTop, offsetLeft }
  const [step, setStep] = useState(0.5);
  const [showGuides, setShowGuides] = useState(false);
  const [showTemplate, setShowTemplate] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/settings/alignment")
      .then((r) => setOffset({ offsetTop: r.data?.offsetTop || 0, offsetLeft: r.data?.offsetLeft || 0 }))
      .catch(() => setOffset({ offsetTop: 0, offsetLeft: 0 }));
  }, []);

  if (!offset) {
    return <div className="p-10 flex items-center gap-2 text-slate-500"><Loader2 className="h-5 w-5 animate-spin" /> Loading…</div>;
  }

  const align = { ...cloneAlign(DEFAULT_ALIGNMENT), offsetTop: offset.offsetTop, offsetLeft: offset.offsetLeft };
  const nudge = (dt, dl) => setOffset((o) => ({ offsetTop: Number((o.offsetTop + dt).toFixed(2)), offsetLeft: Number((o.offsetLeft + dl).toFixed(2)) }));
  const reset = () => setOffset({ offsetTop: 0, offsetLeft: 0 });

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/settings/alignment", offset);
      toast.success("Alignment saved — every future print will use these settings");
    } catch {
      toast.error("Could not save alignment");
    } finally {
      setSaving(false);
    }
  };

  const padBtn = "h-11 w-11 flex items-center justify-center rounded-lg bg-white border border-slate-200 hover:bg-sky-50 hover:border-sky-300 text-slate-700 active:scale-95 transition";

  return (
    <div className="flex h-screen">
      <div className="no-print w-80 shrink-0 border-r border-slate-200 bg-white overflow-y-auto">
        <div className="px-5 py-4 border-b border-slate-200">
          <h2 className="font-app-head font-bold text-slate-800 flex items-center gap-2"><Move className="h-5 w-5 text-[#0284c7]" /> Print Alignment</h2>
          <p className="text-xs text-slate-500 mt-1 leading-relaxed">
            Positions are pre-measured from your prescription paper. Do a test print, then use the nudge pad below to shift everything slightly if needed.
          </p>
        </div>

        <div className="p-5 space-y-6">
          <div>
            <div className="text-xs font-bold uppercase tracking-wide text-slate-500 mb-3">Nudge everything</div>
            <div className="grid grid-cols-3 gap-2 w-max mx-auto">
              <div></div>
              <button className={padBtn} onClick={() => nudge(-step, 0)} data-testid="nudge-up"><ArrowUp className="h-5 w-5" /></button>
              <div></div>
              <button className={padBtn} onClick={() => nudge(0, -step)} data-testid="nudge-left"><ArrowLeft className="h-5 w-5" /></button>
              <button className="h-11 w-11 flex items-center justify-center rounded-lg bg-slate-100 text-[10px] font-semibold text-slate-500" onClick={reset} data-testid="nudge-center" title="Reset to 0">0,0</button>
              <button className={padBtn} onClick={() => nudge(0, step)} data-testid="nudge-right"><ArrowRight className="h-5 w-5" /></button>
              <div></div>
              <button className={padBtn} onClick={() => nudge(step, 0)} data-testid="nudge-down"><ArrowDown className="h-5 w-5" /></button>
              <div></div>
            </div>
          </div>

          <div>
            <div className="text-xs font-bold uppercase tracking-wide text-slate-500 mb-2">Step size</div>
            <div className="flex gap-2">
              {[0.5, 1, 2].map((s) => (
                <button key={s} onClick={() => setStep(s)} data-testid={`step-${s}`}
                  className={`px-3 py-1.5 rounded-lg text-sm font-medium border ${step === s ? "bg-[#0284c7] text-white border-[#0284c7]" : "bg-white text-slate-600 border-slate-200"}`}>
                  {s} mm
                </button>
              ))}
            </div>
          </div>

          <div className="rounded-lg bg-slate-50 p-3 text-sm">
            <div className="flex justify-between py-0.5"><span className="text-slate-500">Vertical shift</span><span className="font-semibold text-slate-800" data-testid="offset-top-value">{offset.offsetTop} mm</span></div>
            <div className="flex justify-between py-0.5"><span className="text-slate-500">Horizontal shift</span><span className="font-semibold text-slate-800" data-testid="offset-left-value">{offset.offsetLeft} mm</span></div>
          </div>

          <div className="space-y-2">
            <label className="flex items-center gap-2 text-sm text-slate-600 cursor-pointer">
              <input type="checkbox" checked={showTemplate} onChange={(e) => setShowTemplate(e.target.checked)} data-testid="toggle-template" /> Show paper design behind
            </label>
            <label className="flex items-center gap-2 text-sm text-slate-600 cursor-pointer">
              <input type="checkbox" checked={showGuides} onChange={(e) => setShowGuides(e.target.checked)} data-testid="toggle-guides" /> Show 10 mm grid
            </label>
          </div>

          <div className="flex gap-2 pt-2 border-t border-slate-100">
            <Button onClick={save} disabled={saving} data-testid="save-alignment-btn" className="bg-[#0284c7] hover:bg-[#0369a1] text-white flex-1">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Save className="h-4 w-4 mr-1" /> Save</>}
            </Button>
            <Button variant="outline" onClick={reset} data-testid="reset-alignment-btn"><RotateCcw className="h-4 w-4" /></Button>
          </div>
        </div>
      </div>

      <div className="flex-1 app-canvas overflow-auto py-8 flex justify-center">
        <div className="relative bg-white" style={{ width: "210mm", boxShadow: "0 20px 60px -15px rgba(0,0,0,0.18)" }}>
          <PreprintLayer data={SAMPLE} align={align} editable={false} showGuides={showGuides} transparent templateSrc={showTemplate ? "/prescription_template.png" : null} />
        </div>
      </div>
    </div>
  );
}
